import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useAgpMutations, useAgpQuery } from "../hooks/useAgpApi";
import { AttainmentBar, EditableCell, ElapsedNote, PaceChip, SourceTag, formatTarget } from "../components/AgpUi";
import ActualsPanel from "../components/ActualsPanel";
import { MeetingRatesStrip } from "../components/MeetingRates";
import { Avatar, Card, EmptyState, ErrorState, Pill, Skeleton, TeamDot } from "../../components/Ui";
import { avatarSrc } from "../../hooks/useBdApi";
import { teamColor } from "../../metrics";

export default function AgpMemberPage({ agpYear }) {
  const { sfUserId } = useParams();
  const params = useMemo(() => ({ year: agpYear.year }), [agpYear.year]);
  const { data, loading, error, refetch } = useAgpQuery(`members/${sfUserId}`, params);
  const mutations = useAgpMutations();

  if (error) return <ErrorState message={error} onRetry={refetch} />;
  if (loading && !data) return <Skeleton height={400} />;

  const { member, rows, goals, window, isMe, meetingRates } = data;
  const year = agpYear.year;
  const color = teamColor(member.team);

  return (
    <div className="bd-page bd-agp" style={{ "--team-color": color }}>
      <div className="bd-member-head">
        <Avatar name={member.name} team={member.team} size="xl" src={avatarSrc(member)} />
        <div className="bd-member-head__text">
          <div className="bd-eyebrow">
            {member.team ? (
              <Link to={`/landmark/bd/agp/team/${member.teamKey}${agpYear.search}`} className="bd-link">
                <TeamDot team={member.team} /> {member.team}
              </Link>
            ) : (
              <span className="bd-muted">No BD team</span>
            )}
          </div>
          <h1>
            {isMe ? "My AGP goals" : `${member.name} · AGP`} {year}
            {!member.isActive && (
              <Pill tone="neutral" className="bd-ml-2">
                inactive
              </Pill>
            )}
          </h1>
          <p className="bd-page__lede">
            {[member.title, member.roleName].filter(Boolean).join(" · ") || member.name} · <ElapsedNote window={window} />
          </p>
        </div>
        <div className="bd-member-head__side">
          <Link to={`/landmark/bd/member/${member.sfUserId}`} className="bd-btn bd-btn--ghost">
            Performance view →
          </Link>
        </div>
      </div>

      <MeetingRatesStrip rates={meetingRates} window={window} />

      {!rows.length && <EmptyState title="No goals for this year" />}

      {rows.map((row) => {
        const headline = ["clientMeetings", "taps", "dealsClosed", "curateAttendees", "signedClients"]
          .map((k) => row.drivers.find((d) => d.key === k))
          .filter((d) => d && d.target !== null && d.target !== undefined);
        return (
          <div key={row.teamKey} className="bd-agp-row">
            <div className="bd-section-title">
              <h2>
                <TeamDot team={row.teamName} /> {row.practice} sheet
                {row.virtual && (
                  <Pill tone="neutral" className="bd-ml-2">
                    not saved yet
                  </Pill>
                )}
              </h2>
              <span className="bd-section-title__meta">
                <Link to={`/landmark/bd/agp/team/${row.teamKey}${agpYear.search}`} className="bd-link">
                  open team sheet →
                </Link>
                {row.canEdit && <span className="bd-ml-2">click a target to edit</span>}
              </span>
            </div>

            {headline.length > 0 && (
              <div className="bd-agp__headline">
                {headline.map((d) => (
                  <div key={d.key} className="bd-agp__headline-item">
                    <div className="bd-agp__headline-label">
                      {d.label} <PaceChip row={d} compact />
                    </div>
                    <div className="bd-agp__headline-value">
                      <strong>{d.actual === null ? "—" : formatTarget(d.actual, d.format)}</strong>
                      <span className="bd-muted"> / {formatTarget(d.target, d.format)}</span>
                    </div>
                    <AttainmentBar row={d} />
                  </div>
                ))}
              </div>
            )}

            <div className="bd-grid bd-grid--1-1 bd-agp-goal-grid">
              {goals.map((g) => {
                const drivers = row.drivers.filter((d) => d.goal === g.number);
                if (!drivers.length) return null;
                const anySet = drivers.some((d) => (d.format === "text" ? d.targetText : d.target !== null && d.target !== undefined));
                if (!anySet && !row.canEdit) return null;
                return (
                  <Card key={g.number} title={`Goal ${g.number}: ${g.title}`} flush>
                    <table className="bd-table bd-agp-table bd-agp-member-table">
                      <tbody>
                        {drivers.map((d) => {
                          const isText = d.format === "text";
                          const hidden = !row.canEdit && (isText ? !d.targetText : d.target === null || d.target === undefined);
                          if (hidden) return null;
                          return (
                            <tr key={d.key}>
                              <td className="bd-agp-member-table__label">
                                <span className="bd-agp-driver">
                                  <span>{d.label}</span>
                                  <SourceTag source={d.source} driverKey={d.key} label={d.label} />
                                </span>
                                {d.note && <small className="bd-muted bd-agp-note">{d.note}</small>}
                              </td>
                              <td className="bd-agp-member-table__target">
                                <EditableCell
                                  value={isText ? d.targetText : d.target}
                                  format={d.format}
                                  canEdit={row.canEdit}
                                  align={isText ? "left" : "right"}
                                  onSave={(v) => mutations.putMember(member.sfUserId, year, row.teamKey, { drivers: [{ key: d.key, ...(isText ? { targetText: v } : { target: v }) }] })}
                                />
                              </td>
                              {!isText && (
                                <td className="bd-agp-member-table__progress">
                                  {d.target !== null && d.target !== undefined ? (
                                    <div className="bd-agp-cell">
                                      <div className="bd-agp-cell__values">
                                        <strong>{d.actual === null || d.actual === undefined ? "—" : formatTarget(d.actual, d.format)}</strong>
                                        <PaceChip row={d} compact />
                                      </div>
                                      <AttainmentBar row={d} />
                                    </div>
                                  ) : (
                                    <span className="bd-muted">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Card>
                );
              })}
            </div>

            <ActualsPanel
              title={`Record actuals · ${row.practice}`}
              subtitle="Manually tracked drivers for this person (sales leads, MQLs, content, …)"
              drivers={row.drivers}
              entries={row.manualActuals}
              canEdit={row.canEdit}
              onAdd={(body) => mutations.addActual({ year, scopeType: "member", teamKey: row.teamKey, sfUserId: member.sfUserId, ...body })}
              onDelete={(id) => mutations.deleteActual(id)}
            />
          </div>
        );
      })}
    </div>
  );
}
